/**
 *
 * SelectAccount
 *
 */

import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from './messages';
import { Column, Row, Wrap } from '../../assets/styledAssets/grid';
import {
  accentColorPrim,
  fontColorPrim,
  fontColorSecond,
  white,
} from '../../assets/styledAssets/vars';
import { Logo } from '../Icons/Icons.component';
import Button from '../Button/Button.component';
import { StyledDropDownList, StyledLabel } from '../FormField/styles';
import { StyledColumn, StyledContainer, StyledRow } from './styles';
import DialogWrap from '../DiaogWrap/DiaogWrap.component';
import AddEditSpace from '../../features/Spaces/components/AddEditSpace/AddEditSpace.component';
import { Account, AccountType, Roles } from '../../domains/app';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../../app/store';
import { setSelectedAccount } from '../../features/App/App.slice';
import { createSpace } from '../../service/Space.service';
import { Space } from '../../features/Spaces/spaces';
import { useHistory } from 'react-router';
import { fetchListAccounts } from '../../service/Persons.service';
import { fetchCurrentPlan } from '../../features/Settings/Settings.actions';
import {
  setSelectedColumns,
  setSelectedDefinition,
  setSelectedExercise,
  setSelectedSummaryColumns,
  setSelectedUser,
} from '../../features/TestResults/TestResults.slice';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog.component';
import { ConfirmationBtnText } from '../../app/constants';

const connector = connect(
  (state: RootState) => ({
    selectedAccount: state.app.selectedAccount,
    user: state.app.user,
  }),
  {
    setSelectedAccount,
    fetchCurrentPlan,
    setSelectedColumns,
    setSelectedSummaryColumns,
    setSelectedUser,
    setSelectedExercise,
    setSelectedDefinition,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const SelectAccount: FunctionComponent<PropsFromRedux> = ({
  selectedAccount,
  setSelectedAccount,
  user,
  fetchCurrentPlan,
  setSelectedColumns,
  setSelectedSummaryColumns,
  setSelectedUser,
  setSelectedExercise,
  setSelectedDefinition,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const [listAccounts, setListAccounts] = useState<Account[]>([]);
  const [showAddEditSpace, setShowAddEditSpace] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState<string>('');

  const getListAccounts = useCallback(() => {
    fetchListAccounts().then((res) => {
      const listAcc: Account[] = res.map((s) => ({
        ...s,
        type: s.role & Roles.User ? AccountType.PERSONAL : AccountType.SPACE,
      }));
      setListAccounts(listAcc);
    });
    setSelectedColumns([]);
    setSelectedSummaryColumns([]);
    setSelectedUser([]);
    setSelectedExercise();
    setSelectedDefinition();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user?.id) {
      getListAccounts();
    }
    // eslint-disable-next-line
  }, [user]);

  const handleChangeAccount = (e: any) => {
    setSelectedAccount(e.target.value);
    if (
      e.target.value.role & Roles.SpaceAdministrator ||
      e.target.value.role & Roles.SpaceOwner
    ) {
      fetchCurrentPlan();
    }
    history.push('/');
  };

  const handleAddNewSpace = () => {
    const unpaidAccounts = listAccounts.filter((a) => !a.paid);
    if (unpaidAccounts.length >= 1) {
      setShowConfirmation(true);
      setConfirmationText(intl.formatMessage(messages.upgradeSpace));
    } else {
      setShowAddEditSpace(true);
    }
  };

  const handleAddEditSpace = useCallback(
    (s: Partial<Space>) => {
      if (user) {
        const space: Space = {
          name: s.name as string,
          ownerId: user.id as string,
        };
        createSpace(space).then(() => {
          getListAccounts();
          setShowAddEditSpace(false);
        });
      }
    },
    [user, getListAccounts],
  );

  const itemRender = (li: any, itemProps: any) => {
    return React.cloneElement(li, {
      ...li.props,
      onClick: () =>
        handleChangeAccount({ target: { value: itemProps.dataItem } }),
    });
  };

  return (
    <StyledContainer>
      <StyledRow>
        <StyledColumn flexCol sm={35} md={30}>
          <Wrap
            flexDir={'column'}
            grow={'1'}
            justifyCont={'space-between'}
            padding={['7rem 2rem', '7rem 4rem']}
            maxWidth={'36rem'}
          >
            <Row>
              <Column md={'hidden'} sm={'hidden'} lg={'hidden'}>
                <Logo color={white} width={'100%'} />
              </Column>
              <Column xs={'hidden'}>
                <Logo color={fontColorPrim} width={'100%'} />
              </Column>
            </Row>
            <Wrap
              grow={'1'}
              width={'100%'}
              justifyCont={'flex-start'}
              flexDir={'column'}
            >
              <Wrap
                fw={'700'}
                fs={'4rem'}
                color={[white, accentColorPrim]}
                margin={'0 0 2rem 0'}
              >
                <FormattedMessage {...messages.title} />
              </Wrap>
              <Wrap
                color={[white, fontColorSecond]}
                fs={'1.4rem'}
                margin={'0 0 6rem 0'}
              >
                <FormattedMessage {...messages.subtitle} />
              </Wrap>
              <Wrap
                bgColor={white}
                br={'1.2rem'}
                flexDir={'column'}
                width={'100%'}
                padding={['2rem', 0]}
              >
                <Wrap width={'100%'}>
                  <StyledLabel>
                    {intl.formatMessage(messages.title)}
                  </StyledLabel>
                  <StyledDropDownList
                    itemRender={itemRender}
                    data={listAccounts}
                    textField={'name'}
                    dataItemKey={'id'}
                    value={selectedAccount}
                  />
                </Wrap>
                <Wrap margin={'2rem 0 0 0'} width={'100%'}>
                  <Button btn primary onClick={handleAddNewSpace}>
                    {intl.formatMessage(messages.createNewSpace)}
                  </Button>
                </Wrap>
              </Wrap>
            </Wrap>
            <Wrap color={[white, fontColorSecond]} fs={'1.4rem'}>
              {intl.formatMessage(messages.footer)}
            </Wrap>
          </Wrap>
        </StyledColumn>
      </StyledRow>
      {showAddEditSpace && (
        <DialogWrap
          title={intl.formatMessage(messages.addSpace)}
          children={
            <AddEditSpace
              onCancel={() => setShowAddEditSpace(!showAddEditSpace)}
              onSave={handleAddEditSpace}
            />
          }
        />
      )}
      {showConfirmation && (
        <ConfirmationDialog
          bodyText={confirmationText}
          handleCancel={() => setShowConfirmation(false)}
          handleConfirm={() => setShowConfirmation(false)}
          confirmBtnText={ConfirmationBtnText.OK}
          hideCancelBtn
        />
      )}
    </StyledContainer>
  );
};

export default connector(memo(SelectAccount));
